import { useTranslation } from 'react-i18next';
import { useAllTrendStoreItems } from '@/core/hooks/useAllTrendStoreItems.hook';
import { addTrendItem } from '@/trendData/trend.actions';
import { isTrendable } from '@/utilities/utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import { faChartArea } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcRestore } from '@seeqdev/icons/seeq';
import { Icon } from '@seeqdev/qomponents';
import type { BaseOutput } from '@seeqdev/api/model/BaseOutput';

interface LockErrorItemLabelProps {
  /** The item that could not be locked (`LockErrorOutputV1.item`). */
  item: BaseOutput;
  itemName: string;
}

/**
 * The name of a lock-blocking item, plus the actions that can be taken on it directly from the Lock tab: trend it in
 * the details pane, or restore it from the trash.
 *
 * Extracted from `LockFailureReasons.atom.tsx` so that a grouped workbook section can list its items - with the same
 * name treatment and the same actions - without also repeating a per-item failure reason under each one.
 */
export const LockErrorItemLabel = ({ item, itemName }: LockErrorItemLabelProps) => {
  const { t } = useTranslation();
  const detailsPaneItems = useAllTrendStoreItems();

  return (
    <>
      <strong data-testid={`globalItemName-${itemName}`} className={item.isArchived ? 'tw:italic' : undefined}>
        {item.type === SeeqNames.Types.Workstep
          ? t('ACCESS_CONTROL.LOCKING.ERROR_REASONS.WORKSTEP_FROM_ANOTHER_ANALYSIS')
          : itemName}
      </strong>
      {item.effectivePermissions?.read &&
      !detailsPaneItems.find((trendItem) => trendItem.id === item.id) &&
      isTrendable(item) ? (
        <Icon
          tooltip={t('SEARCH_DATA.TREND_ITEM')}
          icon={faChartArea}
          extraClassNames="tw:ml-sq-7"
          testId="lockedModalTrendItem"
          onClick={async () => {
            const itemAndProperties = await sqItemsApi.getItemAndAllProperties({ id: item.id });
            addTrendItem(itemAndProperties.data);
          }}
        />
      ) : null}
      {item.isArchived && item.effectivePermissions?.write ? (
        <Icon
          tooltip={t('ACCESS_CONTROL.LOCKING.RESTORE')}
          icon={fcRestore}
          extraClassNames="tw:ml-sq-7"
          testId="lockedModalRestoreItem"
          onClick={() => {
            sqItemsApi.setProperty({ value: false }, { id: item.id, propertyName: SeeqNames.Properties.Archived });
          }}
        />
      ) : null}
    </>
  );
};
